import axios, { AxiosInstance } from 'axios';
import {
  Group, MyGroup, Participant, Member, Match, MatchPreview, SurveyResponse,
  GiftIdea, MyMatch, AnonMessage, UserProfile,
} from '../types';

/** Pull a readable message out of an axios error. */
export const errorMessage = (error: any, fallback = 'Something went wrong'): string =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && localStorage.getItem('authToken')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userEmail');
          const here = window.location.pathname + window.location.search;
          window.location.href = `/?redirect=${encodeURIComponent(here)}&expired=1`;
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async sendMagicLink(email: string, redirect?: string | null): Promise<{ message: string }> {
    const response = await this.client.post('/auth/magic-link', { email, redirect });
    return response.data;
  }

  async verifyMagicLink(token: string): Promise<{ token: string; email: string; redirect?: string | null }> {
    const response = await this.client.post('/auth/verify', { token });
    return response.data;
  }

  async verifyShortLink(code: string): Promise<{ token: string; email: string; groupId: string }> {
    const response = await this.client.post('/auth/short-link', { code });
    return response.data;
  }

  // Me
  async getMe(): Promise<UserProfile> {
    return (await this.client.get('/me')).data;
  }

  async updateMe(data: { name?: string }): Promise<UserProfile> {
    return (await this.client.put('/me', data)).data;
  }

  async getMyGroups(): Promise<MyGroup[]> {
    return (await this.client.get('/me/groups')).data;
  }

  async setPhone(phone: string, consent: boolean): Promise<{ message: string; phone: string }> {
    return (await this.client.post('/me/phone', { phone, consent })).data;
  }

  async verifyPhone(code: string): Promise<UserProfile> {
    return (await this.client.post('/me/phone/verify', { code })).data;
  }

  async setSmsOptIn(optIn: boolean): Promise<UserProfile> {
    return (await this.client.put('/me/sms', { optIn })).data;
  }

  async removePhone(): Promise<UserProfile> {
    return (await this.client.delete('/me/phone')).data;
  }

  // Groups
  async getGroups(): Promise<Group[]> {
    return (await this.client.get('/groups')).data;
  }

  async getGroup(groupId: string): Promise<Group> {
    return (await this.client.get(`/groups/${groupId}`)).data;
  }

  async createGroup(data: {
    name: string;
    year: number;
    settings?: any;
    budget?: number | null;
    exchangeDate?: string | null;
    exchangeLocation?: string | null;
  }): Promise<Group> {
    return (await this.client.post('/groups', data)).data;
  }

  async updateGroup(groupId: string, data: Partial<Group>): Promise<Group> {
    return (await this.client.put(`/groups/${groupId}`, data)).data;
  }

  async deleteGroup(groupId: string): Promise<void> {
    await this.client.delete(`/groups/${groupId}`);
  }

  async createNextYear(groupId: string, data: { year?: number; name?: string } = {}): Promise<Group> {
    return (await this.client.post(`/groups/${groupId}/next-year`, data)).data;
  }

  // Participants
  async getParticipants(groupId: string): Promise<Participant[]> {
    return (await this.client.get(`/groups/${groupId}/participants`)).data;
  }

  async getMembers(groupId: string): Promise<Member[]> {
    return (await this.client.get(`/groups/${groupId}/members`)).data;
  }

  async addParticipant(
    groupId: string,
    data: { name: string; email: string; exclusions?: string[]; householdId?: string | null }
  ): Promise<Participant> {
    return (await this.client.post(`/groups/${groupId}/participants`, data)).data;
  }

  async updateParticipant(groupId: string, participantId: string, data: Partial<Participant>): Promise<Participant> {
    return (await this.client.put(`/groups/${groupId}/participants/${participantId}`, data)).data;
  }

  async deleteParticipant(groupId: string, participantId: string): Promise<void> {
    await this.client.delete(`/groups/${groupId}/participants/${participantId}`);
  }

  async importParticipants(
    groupId: string,
    participants: Array<{ name: string; email: string; household?: string; exclusions?: string[] }>
  ): Promise<{ created: Participant[]; skipped: Array<{ row: any; reason: string }> }> {
    return (await this.client.post(`/groups/${groupId}/participants/import`, { participants })).data;
  }

  // Matches
  async generateMatches(
    groupId: string,
    options: { dryRun?: boolean; confirmRegenerate?: boolean } = {}
  ): Promise<{ message: string; matches?: MatchPreview[]; matchCount?: number; notified?: number; failed?: string[] }> {
    return (await this.client.post(`/groups/${groupId}/matches`, options)).data;
  }

  async getMatchHistory(groupId: string): Promise<Match[]> {
    return (await this.client.get(`/groups/${groupId}/matches/history`)).data;
  }

  async resendMatch(groupId: string, participantId: string): Promise<{ message: string }> {
    return (await this.client.post(`/groups/${groupId}/matches/${participantId}/resend`, {})).data;
  }

  async getMyMatch(groupId: string): Promise<MyMatch> {
    return (await this.client.get(`/groups/${groupId}/my-match`)).data;
  }

  // Gift ideas
  async getGiftIdeas(groupId: string, params: { subject?: string; mine?: boolean } = {}): Promise<GiftIdea[]> {
    return (await this.client.get(`/groups/${groupId}/gift-ideas`, {
      params: { subject: params.subject, mine: params.mine ? 1 : undefined },
    })).data;
  }

  async getGiftIdeaSummary(groupId: string): Promise<Record<string, number>> {
    return (await this.client.get(`/groups/${groupId}/gift-ideas/summary`)).data;
  }

  async createGiftIdea(
    groupId: string,
    data: { subjectParticipantId: string; title: string; url?: string; notes?: string; priceEstimate?: string | number | null }
  ): Promise<GiftIdea> {
    return (await this.client.post(`/groups/${groupId}/gift-ideas`, data)).data;
  }

  async updateGiftIdea(groupId: string, ideaId: string, data: Partial<GiftIdea>): Promise<GiftIdea> {
    return (await this.client.put(`/groups/${groupId}/gift-ideas/${ideaId}`, data)).data;
  }

  async deleteGiftIdea(groupId: string, ideaId: string): Promise<void> {
    await this.client.delete(`/groups/${groupId}/gift-ideas/${ideaId}`);
  }

  // Anonymous Q&A
  async getMessages(groupId: string): Promise<{ asGiver: AnonMessage[]; asReceiver: AnonMessage[] }> {
    return (await this.client.get(`/groups/${groupId}/messages`)).data;
  }

  async sendMessage(groupId: string, body: string, as: 'giver' | 'receiver'): Promise<AnonMessage> {
    return (await this.client.post(`/groups/${groupId}/messages`, { body, as })).data;
  }

  // Survey
  async getSurveyQuestions(groupId: string): Promise<any[]> {
    return (await this.client.get(`/survey/${groupId}/questions`)).data;
  }

  async getSurveyResponse(participantId: string): Promise<SurveyResponse | null> {
    try {
      return (await this.client.get(`/survey/response/${participantId}`)).data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  async submitSurveyResponse(participantId: string, groupId: string, responses: Record<string, any>): Promise<SurveyResponse> {
    return (await this.client.post('/survey/response', { participantId, groupId, responses })).data;
  }
}

export const apiService = new ApiService();
export default apiService;
